import { useDraggable } from "@dnd-kit/core";
import { useMemo } from "react";
import { Pencil, Trash2, ArrowRight, Flag } from "lucide-react";

const quadrantOptions = [
  { id: "hi-impact-lo-effort", label: "High / Low Effort", group: "High Impact", color: "hsl(142, 55%, 72%)" },
  { id: "hi-impact-med-effort", label: "High / Med Effort", group: "High Impact", color: "hsl(142, 45%, 65%)" },
  { id: "hi-impact-hi-effort", label: "High / High Effort", group: "High Impact", color: "hsl(39, 88%, 72%)" },
  { id: "med-impact-lo-effort", label: "Med / Low Effort", group: "Med Impact", color: "hsl(39, 70%, 68%)" },
  { id: "med-impact-med-effort", label: "Med / Med Effort", group: "Med Impact", color: "hsl(39, 55%, 62%)" },
  { id: "med-impact-hi-effort", label: "Med / High Effort", group: "Med Impact", color: "hsl(30, 50%, 60%)" },
  { id: "lo-impact-lo-effort", label: "Low / Low Effort", group: "Low Impact", color: "hsl(210, 55%, 74%)" },
  { id: "lo-impact-med-effort", label: "Low / Med Effort", group: "Low Impact", color: "hsl(210, 40%, 68%)" },
  { id: "lo-impact-hi-effort", label: "Low / High Effort", group: "Low Impact", color: "hsl(0, 55%, 74%)" },
];

export default function TaskBubble({ task, isInMatrix, onEdit, onDelete, onMoveToQuadrant, onToggleUrgent }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });
  const subtasks = useMemo(() => task.subtasks || [], [task.subtasks]);
  const completedCount = useMemo(() => subtasks.filter((s) => s.completed).length, [subtasks]);
  const isUrgent = task.urgent || false;
  const availableQuadrants = useMemo(
    () => isInMatrix ? quadrantOptions.filter(q => q.id !== task.quadrant) : quadrantOptions,
    [isInMatrix, task.quadrant]
  );

  return (
    <div
      ref={setNodeRef}
      className={`task-bubble task-bubble-${task.category} ${isInMatrix ? "matrix" : "inbox"} ${isDragging ? "is-dragging" : ""} ${isUrgent ? "urgent" : ""}`}
      {...attributes}
      {...listeners}
      data-testid="task-bubble-draggable"
    >
      <span className={`bubble-dot dot-${task.category}`} />
      <span className="bubble-name">{task.name}</span>
      {subtasks.length > 0 && (
        <span className="bubble-subtask-badge" data-testid="subtask-badge">
          {completedCount}/{subtasks.length}
        </span>
      )}
      {isUrgent && <Flag size={11} className="bubble-urgent-icon" />}
      <div className="bubble-actions">
        {onToggleUrgent && (
          <button
            className={`bubble-action-btn urgent-flag-btn ${isUrgent ? "active" : ""}`}
            onClick={(e) => { e.stopPropagation(); onToggleUrgent(task.id); }}
            title={isUrgent ? "Remove urgent" : "Mark urgent"}
            aria-label={isUrgent ? `Remove urgent from ${task.name}` : `Mark ${task.name} as urgent`}
            data-testid="task-urgent-toggle"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Flag size={12} />
          </button>
        )}
        <button
          className="bubble-action-btn"
          onClick={(e) => { e.stopPropagation(); onEdit(task); }}
          title="Edit"
          aria-label={`Edit task ${task.name}`}
          data-testid="task-edit-button"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Pencil size={12} />
        </button>
        {/* Move-to-quadrant menu */}
        <div className="bubble-move-menu" onPointerDown={(e) => e.stopPropagation()}>
          <button className="bubble-action-btn" title="Move" data-testid="task-move-button">
            <ArrowRight size={12} />
          </button>
          <div className="bubble-move-dropdown" data-testid="task-move-dropdown">
            {!isInMatrix && availableQuadrants.map((q) => (
              <button
                key={q.id}
                className="move-option"
                style={{ borderLeftColor: q.color }}
                onClick={(e) => { e.stopPropagation(); onMoveToQuadrant(task.id, q.id); }}
              >
                {q.label}
              </button>
            ))}
            {isInMatrix && (
              <>
                <button
                  className="move-option move-option-inbox"
                  onClick={(e) => { e.stopPropagation(); onMoveToQuadrant(task.id, null); }}
                >
                  Back to Inbox
                </button>
                {availableQuadrants.map((q) => (
                  <button
                    key={q.id}
                    className="move-option"
                    style={{ borderLeftColor: q.color }}
                    onClick={(e) => { e.stopPropagation(); onMoveToQuadrant(task.id, q.id); }}
                  >
                    {q.label}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
        <button
          className="bubble-action-btn delete-btn"
          onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
          title="Delete"
          aria-label={`Delete task ${task.name}`}
          data-testid="task-delete-button"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}
